import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  ArrowLeft,
  CheckCircle,
  Calendar,
  Clock,
  MapPin,
  Home,
  Building,
  Droplets,
  Wind,
  Sun,
  Zap,
  Sparkles,
  Square,
  Bed,
  ShieldCheck,
  User,
  Phone,
  Mail,
  MessageCircle,
  Shield,
  Check,
  ChevronRight,
  CreditCard,
  Users,
  Award,
  Truck,
  Thermometer,
} from "lucide-react";

const BookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    // Step 1: Service Details
    service: null,
    serviceType: "",
    package: null,
    
    // Step 2: Personal Information
    name: "",
    phone: "",
    email: "",
    address: "",
    suburb: "",
    
    // Step 3: Booking Details
    serviceDate: "",
    serviceTime: "",
    recurring: "One-time",
    
    // Step 4: Additional Information
    propertySize: "",
    bedrooms: "",
    bathrooms: "",
    specialRequests: "",
    notes: "",
    accessInstructions: "",
    pets: "No",
    
    // Step 5: Confirmation
    agreeTerms: false,
    marketingEmails: false,
  });

  // Service data matching the ServicesPage
  const services = {
    "residential-cleaning": {
      id: 1,
      icon: Home,
      title: "Residential Cleaning",
      category: "Home Cleaning",
      description: "Complete home cleaning for sparkling results.",
      color: "from-blue-500 to-indigo-600",
      stats: { time: "2-4 hours", coverage: "All Rooms", guarantee: "100% Satisfaction" },
      pricing: [
        { id: "res-1", name: "Studio/1 Bedroom", price: "$129", details: "Perfect for apartments" },
        { id: "res-2", name: "2 Bedroom Home", price: "$199", details: "Most popular for families", bestValue: true },
        { id: "res-3", name: "3 Bedroom Home", price: "$249", details: "Complete home clean" },
        { id: "res-4", name: "4+ Bedroom Home", price: "Custom Quote", details: "Large homes & properties" },
      ],
    },
    "commercial-cleaning": {
      id: 2,
      icon: Building,
      title: "Commercial Cleaning",
      category: "Business Cleaning",
      description: "Professional office & workspace solutions.",
      color: "from-emerald-500 to-teal-600",
      stats: { time: "Custom Hours", coverage: "Entire Facility", guarantee: "Insurance Backed" },
      pricing: [
        { id: "com-1", name: "Small Office", price: "$299/week", details: "Up to 1000 sq ft", bestValue: true },
        { id: "com-2", name: "Medium Office", price: "$499/week", details: "1000-3000 sq ft" },
        { id: "com-3", name: "Large Office", price: "$899/week", details: "3000-5000 sq ft" },
        { id: "com-4", name: "Industrial/Custom", price: "Custom Quote", details: "Tailored solutions" },
      ],
    },
    "carpet-upholstery-cleaning": {
      id: 3,
      icon: Droplets,
      title: "Carpet & Upholstery",
      category: "Fabric Cleaning",
      description: "Expert cleaning for fabrics & carpets.",
      color: "from-amber-500 to-orange-600",
      stats: { time: "1-3 hours", coverage: "Per Room", guarantee: "Stain-Free Promise" },
      pricing: [
        { id: "carpet-1", name: "Per Room", price: "$79 - $129", details: "Based on size & condition" },
        { id: "carpet-2", name: "Large Living Area", price: "$149", details: "Open plan spaces", bestValue: true },
        { id: "carpet-3", name: "Sofa Cleaning", price: "$99 - $199", details: "2-3 seater" },
        { id: "carpet-4", name: "Full House Package", price: "$399", details: "Whole house carpets" },
      ],
    },
    "duct-vent-cleaning": {
      id: 4,
      icon: Wind,
      title: "Duct & Vent Cleaning",
      category: "Air Quality",
      description: "Improve indoor air quality with professional duct cleaning.",
      color: "from-purple-500 to-violet-600",
      stats: { time: "3-6 hours", coverage: "Full System", guarantee: "Air Quality Test" },
      pricing: [
        { id: "duct-1", name: "Standard Home", price: "$299 - $499", details: "Up to 10 vents", bestValue: true },
        { id: "duct-2", name: "Large Home", price: "$599 - $899", details: "10-20 vents" },
        { id: "duct-3", name: "Commercial", price: "Custom Quote", details: "Office buildings" },
        { id: "duct-4", name: "Add-on: Dryer Vent", price: "$99", details: "Fire hazard prevention" },
      ],
    },
    "end-of-lease-cleaning": {
      id: 5,
      icon: Shield,
      title: "End of Lease Cleaning",
      category: "Property Cleaning",
      description: "Get your full bond back with comprehensive cleaning.",
      color: "from-rose-500 to-pink-600",
      stats: { time: "4-8 hours", coverage: "Entire Property", guarantee: "Bond Back" },
      pricing: [
        { id: "bond-1", name: "1 Bedroom Unit", price: "$299", details: "Apartments & units" },
        { id: "bond-2", name: "2 Bedroom Apartment", price: "$399", details: "Standard cleaning", bestValue: true },
        { id: "bond-3", name: "3 Bedroom House", price: "$499", details: "Complete house clean" },
        { id: "bond-4", name: "4+ Bedroom House", price: "Custom Quote", details: "Large properties" },
      ],
    },
    "window-gutter-cleaning": {
      id: 6,
      icon: Sun,
      title: "Window & Gutter Cleaning",
      category: "Exterior Cleaning",
      description: "Crystal clear windows and clean gutters.",
      color: "from-cyan-500 to-blue-600",
      stats: { time: "1-2 hours", coverage: "All Windows", guarantee: "Streak-Free" },
      pricing: [
        { id: "window-1", name: "Standard Home", price: "$149 - $249", details: "Windows & gutters", bestValue: true },
        { id: "window-2", name: "Two-Story Home", price: "$249 - $399", details: "With extension ladders" },
        { id: "window-3", name: "Commercial", price: "Custom Quote", details: "Office buildings" },
        { id: "window-4", name: "Maintenance Plan", price: "$99/month", details: "Quarterly service" },
      ],
    },
    "emergency-restoration": {
      id: 7,
      icon: Zap,
      title: "Emergency Restoration",
      category: "Emergency Services",
      description: "24/7 emergency services for water damage & flood recovery.",
      color: "from-red-500 to-orange-600",
      stats: { time: "Immediate", coverage: "Emergency Response", guarantee: "Insurance Ready" },
      pricing: [
        { id: "emergency-1", name: "Emergency Assessment", price: "$199", details: "Immediate response" },
        { id: "emergency-2", name: "Water Damage", price: "$99/hour", details: "Per technician", bestValue: true },
        { id: "emergency-3", name: "Fire/Smoke Damage", price: "$149/hour", details: "Specialized cleanup" },
        { id: "emergency-4", name: "Insurance Work", price: "Direct Billing", details: "No upfront cost" },
      ],
    },
    "deep-cleaning": {
      id: 8,
      icon: Sparkles,
      title: "Deep Cleaning",
      category: "Specialized Cleaning",
      description: "Intensive cleaning for special occasions or renovations.",
      color: "from-indigo-500 to-purple-600",
      stats: { time: "5-8 hours", coverage: "Complete Home", guarantee: "Deep Clean Guarantee" },
      pricing: [
        { id: "deep-1", name: "2 Bedroom Home", price: "$349", details: "Standard deep clean" },
        { id: "deep-2", name: "3 Bedroom Home", price: "$449", details: "Comprehensive service", bestValue: true },
        { id: "deep-3", name: "4+ Bedroom Home", price: "$549+", details: "Large property" },
        { id: "deep-4", name: "Post-Renovation", price: "Custom Quote", details: "Construction cleanup" },
      ],
    },
    "rug-cleaning": {
      id: 9,
      icon: Square,
      title: "Rug Cleaning",
      category: "Fabric Cleaning",
      description: "Professional cleaning for area rugs and oriental carpets.",
      color: "from-amber-600 to-yellow-600",
      stats: { time: "3-5 hours", coverage: "Per Rug", guarantee: "Color Protection" },
      pricing: [
        { id: "rug-1", name: "Small Rug", price: "$99", details: "Up to 2x3 meters" },
        { id: "rug-2", name: "Medium Rug", price: "$149", details: "2x3 to 3x5 meters", bestValue: true },
        { id: "rug-3", name: "Large Rug", price: "$199", details: "Over 3x5 meters" },
        { id: "rug-4", name: "Antique/Oriental", price: "Custom Quote", details: "Specialist care" },
      ],
    },
    "mattress-cleaning": {
      id: 10,
      icon: Bed,
      title: "Mattress Cleaning",
      category: "Health & Wellness",
      description: "Deep sanitization for healthier sleep environment.",
      color: "from-purple-600 to-pink-600",
      stats: { time: "1-2 hours", coverage: "Per Mattress", guarantee: "Allergen Free" },
      pricing: [
        { id: "mattress-1", name: "Single Mattress", price: "$99", details: "Standard cleaning" },
        { id: "mattress-2", name: "Double Mattress", price: "$129", details: "Thorough sanitization", bestValue: true },
        { id: "mattress-3", name: "Queen Mattress", price: "$149", details: "Complete treatment" },
        { id: "mattress-4", name: "Multiple Mattresses", price: "Package Deal", details: "Discount available" },
      ],
    },
    "rugs-deep-cleaning": {
      id: 11,
      icon: Square,
      title: "Rugs Deep Cleaning",
      category: "Fabric Cleaning",
      description: "Comprehensive deep cleaning for all types of rugs.",
      color: "from-teal-600 to-emerald-600",
      stats: { time: "2-4 hours", coverage: "All Rugs", guarantee: "Deep Clean Guarantee" },
      pricing: [
        { id: "rugsdeep-1", name: "Residential Deep Clean", price: "$149", details: "Per average rug", bestValue: true },
        { id: "rugsdeep-2", name: "Commercial Rug Cleaning", price: "$199", details: "Heavy traffic areas" },
        { id: "rugsdeep-3", name: "Very Large Rugs", price: "$249+", details: "Over 4x6 meters" },
        { id: "rugsdeep-4", name: "Rug Restoration", price: "Custom Quote", details: "Damaged rugs" },
      ],
    },
    "curtain-cleaning": {
      id: 12,
      icon: Square,
      title: "Curtain Cleaning",
      category: "Fabric Cleaning",
      description: "Professional curtain cleaning preserving fabric quality.",
      color: "from-teal-600 to-emerald-600",
      stats: { time: "1-3 hours", coverage: "All Curtain Types", guarantee: "Freshness Guarantee" },
      pricing: [
        { id: "curtain-1", name: "Standard Curtains", price: "$29/panel", details: "Average size", bestValue: true },
        { id: "curtain-2", name: "Delicate Fabrics", price: "$39/panel", details: "Silk, velvet, etc." },
        { id: "curtain-3", name: "Heavy Drapes", price: "$49/panel", details: "Lined or blackout" },
        { id: "curtain-4", name: "Full Service", price: "$199", details: "Complete package" },
      ],
    },
  };

  // Initialize from URL params or location state
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const serviceSlug = searchParams.get('service') || location.state?.serviceSlug;
    
    if (serviceSlug && services[serviceSlug]) {
      setBookingData(prev => ({
        ...prev,
        service: services[serviceSlug],
        serviceType: services[serviceSlug].title
      }));
    }
  }, [location]);

  // Handle form field changes
  const handleChange = (field, value) => {
    setBookingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle package selection
  const handlePackageSelect = (pkg) => {
    setBookingData(prev => ({
      ...prev,
      package: pkg
    }));
  };

  // Handle step navigation
  const nextStep = () => {
    if (step < 5) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo(0, 0);
    }
  };

  // Submit booking via WhatsApp
  const submitBooking = () => {
    const phoneNumber = "61420499661";
    
    const message = `*📋 COMPLETE BOOKING SUBMISSION* 🎯

🏢 *Service Details:*
• Service: ${bookingData.service?.title || bookingData.serviceType}
• Package: ${bookingData.package?.name || "Custom"}
• Price: ${bookingData.package?.price || "Quote required"}
• Details: ${bookingData.package?.details || ""}

👤 *Customer Information:*
• Name: ${bookingData.name}
• Phone: ${bookingData.phone}
• Email: ${bookingData.email}
• Address: ${bookingData.address}, ${bookingData.suburb}

📅 *Booking Details:*
• Date: ${bookingData.serviceDate}
• Time: ${bookingData.serviceTime}
• Frequency: ${bookingData.recurring}

🏠 *Property Details:*
• Size: ${bookingData.propertySize}
• Bedrooms: ${bookingData.bedrooms}
• Bathrooms: ${bookingData.bathrooms}
• Pets: ${bookingData.pets}

📝 *Additional Information:*
• Special Requests: ${bookingData.specialRequests || "None"}
• Access Instructions: ${bookingData.accessInstructions || "None"}
• Notes: ${bookingData.notes || "None"}

✅ *Confirmation:*
• Terms Accepted: ${bookingData.agreeTerms ? "Yes" : "No"}
• Marketing Emails: ${bookingData.marketingEmails ? "Yes" : "No"}

🎯 *Please confirm this booking and provide:*
1. Exact quotation
2. Technician details
3. Confirmation number
4. Payment instructions`;

    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  // Calculate progress percentage
  const progressPercentage = (step / 5) * 100;

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Select Your Service</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(services).map(([slug, service]) => (
                  <button
                    key={slug}
                    onClick={() => handleChange('service', service)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${bookingData.service?.id === service.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-lg bg-gradient-to-r ${service.color} flex items-center justify-center`}>
                        <service.icon size={20} className="text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{service.title}</div>
                        <div className="text-sm text-gray-600">{service.category}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {bookingData.service && (
              <div className="mt-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Select Package</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {bookingData.service.pricing.map((pkg, index) => (
                    <div
                      key={pkg.id}
                      onClick={() => handlePackageSelect(pkg)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${bookingData.package?.id === pkg.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-bold text-gray-900">{pkg.name}</div>
                          <div className="text-sm text-gray-600">{pkg.details}</div>
                        </div>
                        <div className="text-lg font-bold text-blue-600">{pkg.price}</div>
                      </div>
                      {pkg.bestValue && (
                        <div className="inline-block bg-gradient-to-r from-amber-400 to-yellow-400 text-gray-900 text-xs font-bold px-2 py-1 rounded">
                          BEST VALUE
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Personal Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={bookingData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="John Smith"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  required
                  value={bookingData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="04XX XXX XXX"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={bookingData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="john@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Suburb *
                </label>
                <input
                  type="text"
                  required
                  value={bookingData.suburb}
                  onChange={(e) => handleChange('suburb', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="Dandenong"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address *
                </label>
                <input
                  type="text"
                  required
                  value={bookingData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="123 Main Street"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Booking Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Date *
                </label>
                <input
                  type="date"
                  required
                  value={bookingData.serviceDate}
                  onChange={(e) => handleChange('serviceDate', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Time *
                </label>
                <select
                  required
                  value={bookingData.serviceTime}
                  onChange={(e) => handleChange('serviceTime', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="">Select time</option>
                  <option value="Morning (9 AM - 12 PM)">Morning (9 AM - 12 PM)</option>
                  <option value="Afternoon (1 PM - 4 PM)">Afternoon (1 PM - 4 PM)</option>
                  <option value="Evening (5 PM - 8 PM)">Evening (5 PM - 8 PM)</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Frequency
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {["One-time", "Weekly", "Bi-weekly", "Monthly"].map((freq) => (
                    <button
                      key={freq}
                      type="button"
                      onClick={() => handleChange('recurring', freq)}
                      className={`py-3 rounded-lg border-2 transition-all ${bookingData.recurring === freq ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-blue-300'}`}
                    >
                      {freq}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Additional Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Property Size/Type
                </label>
                <select
                  value={bookingData.propertySize}
                  onChange={(e) => handleChange('propertySize', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="">Select property size</option>
                  <option value="Studio/1 Bedroom">Studio/1 Bedroom</option>
                  <option value="2 Bedrooms">2 Bedrooms</option>
                  <option value="3 Bedrooms">3 Bedrooms</option>
                  <option value="4+ Bedrooms">4+ Bedrooms</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Bedrooms
                </label>
                <select
                  value={bookingData.bedrooms}
                  onChange={(e) => handleChange('bedrooms', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="">Select</option>
                  {[1,2,3,4,5,6].map(num => (
                    <option key={num} value={num}>{num} {num === 1 ? 'Bedroom' : 'Bedrooms'}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Bathrooms
                </label>
                <select
                  value={bookingData.bathrooms}
                  onChange={(e) => handleChange('bathrooms', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="">Select</option>
                  {[1,2,3,4,5].map(num => (
                    <option key={num} value={num}>{num} {num === 1 ? 'Bathroom' : 'Bathrooms'}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pets at Property?
                </label>
                <select
                  value={bookingData.pets}
                  onChange={(e) => handleChange('pets', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="No">No pets</option>
                  <option value="Yes - Indoor">Yes - Indoor</option>
                  <option value="Yes - Outdoor">Yes - Outdoor</option>
                  <option value="Yes - Both">Yes - Indoor & Outdoor</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Instructions
                </label>
                <textarea
                  value={bookingData.accessInstructions}
                  onChange={(e) => handleChange('accessInstructions', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  rows="3"
                  placeholder="Key location, security codes, gate instructions, etc."
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Requests
                </label>
                <textarea
                  value={bookingData.specialRequests}
                  onChange={(e) => handleChange('specialRequests', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  rows="3"
                  placeholder="Any specific requirements or areas of focus..."
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  value={bookingData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  rows="3"
                  placeholder="Parking information, allergies, previous issues, etc."
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Review & Confirm</h3>
              
              {/* Service Summary */}
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Service Summary</h4>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service:</span>
                    <span className="font-semibold">{bookingData.service?.title || bookingData.serviceType}</span>
                  </div>
                  {bookingData.package && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Package:</span>
                        <span className="font-semibold">{bookingData.package.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Price:</span>
                        <span className="font-bold text-blue-600">{bookingData.package.price}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Frequency:</span>
                    <span className="font-semibold">{bookingData.recurring}</span>
                  </div>
                </div>
              </div>
              
              {/* Contact Details */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact Details</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <User size={18} className="text-gray-400" />
                    <span>{bookingData.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-gray-400" />
                    <span>{bookingData.phone}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-gray-400" />
                    <span>{bookingData.email}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin size={18} className="text-gray-400" />
                    <span>{bookingData.address}, {bookingData.suburb}</span>
                  </div>
                </div>
              </div>
              
              {/* Booking Details */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Booking Details</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Calendar size={18} className="text-gray-400" />
                    <span>Date: {bookingData.serviceDate || "Not specified"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock size={18} className="text-gray-400" />
                    <span>Time: {bookingData.serviceTime || "Not specified"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Home size={18} className="text-gray-400" />
                    <span>Property: {bookingData.propertySize || "Not specified"}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Terms and Conditions */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={bookingData.agreeTerms}
                  onChange={(e) => handleChange('agreeTerms', e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="terms" className="text-sm text-gray-700">
                  I agree to the Terms and Conditions and understand that final pricing may vary based on property inspection.
                </label>
              </div>
              
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="marketing"
                  checked={bookingData.marketingEmails}
                  onChange={(e) => handleChange('marketingEmails', e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="marketing" className="text-sm text-gray-700">
                  I agree to receive promotional emails and updates from Knight Cleaning.
                </label>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Check if current step is valid
  const isStepValid = () => {
    switch (step) {
      case 1:
        return bookingData.service && bookingData.package;
      case 2:
        return bookingData.name && bookingData.phone && bookingData.email && bookingData.address && bookingData.suburb;
      case 3:
        return bookingData.serviceDate && bookingData.serviceTime;
      case 4:
        return true; // All fields optional
      case 5:
        return bookingData.agreeTerms;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>Book Cleaning Service | Knight Cleaning</title>
        <meta name="description" content="Book professional cleaning services with Knight Cleaning. Residential, commercial, carpet, and specialized cleaning services across Melbourne." />
      </Helmet>

      {/* Progress Bar */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-2">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>
            <div className="text-sm font-medium text-gray-600">
              Step {step} of 5
            </div>
          </div>
          
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          
          <div className="flex justify-between mt-4">
            {["Service", "Details", "Schedule", "Extras", "Confirm"].map((label, index) => (
              <div key={label} className="text-center">
                <div className={`h-8 w-8 rounded-full mx-auto mb-1 flex items-center justify-center ${step > index + 1 ? 'bg-green-500 text-white' : step === index + 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
                  {step > index + 1 ? <Check size={16} /> : index + 1}
                </div>
                <div className={`text-xs font-medium ${step >= index + 1 ? 'text-gray-900' : 'text-gray-400'}`}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {step === 1 && "Select Your Service"}
            {step === 2 && "Your Information"}
            {step === 3 && "Schedule Your Service"}
            {step === 4 && "Additional Details"}
            {step === 5 && "Review & Confirm"}
          </h1>
          <p className="text-gray-600">
            {step === 1 && "Choose the cleaning service and package that best fits your needs"}
            {step === 2 && "Tell us about yourself so we can serve you better"}
            {step === 3 && "Select the most convenient date and time for your service"}
            {step === 4 && "Provide additional information for the best service experience"}
            {step === 5 && "Review your booking details before final submission"}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          {renderStepContent()}
          
          {/* Navigation Buttons */}
          <div className="flex justify-between mt-12 pt-8 border-t border-gray-200">
            {step > 1 ? (
              <button
                onClick={prevStep}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <ArrowLeft size={18} />
                Previous
              </button>
            ) : (
              <div /> // Empty div for spacing
            )}
            
            {step < 5 ? (
              <button
                onClick={nextStep}
                disabled={!isStepValid()}
                className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${isStepValid() ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:scale-105' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
              >
                Continue
                <ChevronRight size={18} />
              </button>
            ) : (
              <button
                onClick={submitBooking}
                disabled={!isStepValid()}
                className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${isStepValid() ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 hover:scale-105' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
              >
                <MessageCircle size={18} />
                Submit via WhatsApp
              </button>
            )}
          </div>
        </div>

        {/* Service Guarantee */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
            <div className="h-12 w-12 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 flex items-center justify-center mx-auto mb-4">
              <ShieldCheck size={24} className="text-green-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Satisfaction Guaranteed</h3>
            <p className="text-sm text-gray-600">100% satisfaction or we'll re-clean free of charge</p>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
            <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 flex items-center justify-center mx-auto mb-4">
              <Users size={24} className="text-blue-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Certified Professionals</h3>
            <p className="text-sm text-gray-600">Background checked, trained, and insured technicians</p>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
            <div className="h-12 w-12 rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 flex items-center justify-center mx-auto mb-4">
              <Award size={24} className="text-amber-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Quality Equipment</h3>
            <p className="text-sm text-gray-600">State-of-the-art cleaning equipment and eco-friendly products</p>
          </div>
        </div>

        {/* Need Help Section */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">Need help or have questions?</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+61420499661"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
            >
              <Phone size={18} />
              Call: +61 420 499 661
            </a>
            <span className="text-gray-300">•</span>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
            >
              <Mail size={18} />
              Email Support
            </a>
            <span className="text-gray-300">•</span>
            <button
              onClick={() => window.open(`https://wa.me/61420499661?text=${encodeURIComponent("Hi, I need help with my booking")}`, "_blank")}
              className="inline-flex items-center gap-2 text-green-600 hover:text-green-800 font-medium"
            >
              <MessageCircle size={18} />
              WhatsApp Chat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;